import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The typographic roles defined in `styles/theme.css`. They must match that
 * file's `--text-*` variables: they are the same names.
 */
const FONT_SIZE_ROLES = [
  'display',
  'h1',
  'h2',
  'h3',
  'h4',
  'lead',
  'body',
  'body-s',
  'caption',
  'kicker',
] as const;

/** Semantic spacing names from `styles/theme.css` (`--spacing-*`). */
const SPACING_ROLES = [
  'inset-sm',
  'inset',
  'inset-lg',
  'stack-sm',
  'stack',
  'stack-lg',
  'gutter',
  'row',
  'row-compact',
  'icon-sm',
  'icon-md',
  'icon-lg',
  'popup-sm',
  'popup-md',
  'popup-lg',
  'touch',
] as const;

/**
 * `twMerge` configurado con nuestras escalas.
 *
 * Without this there is a silent, expensive bug: `tailwind-merge` ships with
 * Tailwind's scale cached, so it does not recognise `text-body-s` as a font
 * size and classifies it as a colour (`text-<color>`). So when combining
 * `text-body-s` with `text-fg-on-action`, it decides they compete and **deletes
 * the first** — the button lost its typographic role and nothing said so: lint
 * does not fail, typecheck does not fail, and on screen you only see a slightly
 * different font size. Found by reading the button's real `className` in the
 * DOM (ver `e2e/tokens.spec.ts`).
 *
 * Since `theme.css`'s reset deletes the stock scale, `override` is the right
 * one and not `extend`: these roles do not add to `text-sm`/`text-lg`, they
 * reemplazan.
 */
const twMerge = extendTailwindMerge({
  override: {
    classGroups: {
      'font-size': [{ text: [...FONT_SIZE_ROLES] }],
    },
  },
  extend: {
    // The semantic names coexist with the numeric scale (`p-4`), which still
    // exists: hence `extend` here and not `override`.
    theme: {
      spacing: [...SPACING_ROLES],
    },
  },
});

/** Combines conditional classes and resolves Tailwind conflicts. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Appends a final period unless the sentence already ends with one.
 *
 * Built for sentences that end in an interpolated razón social: `S.A.` and
 * `Ltda.` already carry a trailing period, so a hardcoded one in the template
 * literal doubles it (`S.A..`). Trailing whitespace is trimmed first so a
 * value like `'Ltda. '` is still recognised as already-terminated.
 */
export function withFinalPeriod(text: string): string {
  const trimmed = text.trimEnd();
  return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}
